import { ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { switchMap } from 'rxjs';
import { CompanyProductObjectiveComponent } from 'src/app/components/main/employees/dashboard/company-product-objective/company-product-objective.component';
import {
  MonthNavigationComponent,
  formatMonthKeyFromDate,
  formatMonthLabelWithTodayHint,
  getNextMonthKey,
  getPreviousMonthKey,
  normalizeMonthKey,
  resolveDashboardDateForMonthKey,
} from 'src/app/components/main/shared/month-navigation/month-navigation.component';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { CompanyProductPageIdentityComponent } from '../company-product-page-identity/company-product-page-identity.component';
import { CompanyMetricComponent } from './company-metric/company-metric.component';
import { CompanyProductInternalMetricsComponent } from './company-product-internal-metrics/company-product-internal-metrics.component';

@Component({
  selector: 'app-company-product-okr-and-metrics',
  templateUrl: './company-product-okr-and-metrics.component.html',
  styleUrls: ['./company-product-okr-and-metrics.component.css'],
  imports: [
    CompanyProductPageIdentityComponent,
    CompanyProductObjectiveComponent,
    CompanyProductInternalMetricsComponent,
    CompanyMetricComponent,
    MonthNavigationComponent,
    MatButtonModule,
    MatIconModule,
    RouterLink,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyProductOkrAndMetricsComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api = inject(ApiService);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly viewport_scroller = inject(ViewportScroller);
  private readonly permissions = inject(PermissionsService);

  readonly company_product_id = Number(
    this.activated_route.parent?.snapshot.paramMap.get('id') ?? this.activated_route.parent?.snapshot.paramMap.get('company_product_id')
  );
  readonly is_worker = this.permissions.isWorker();
  readonly is_product_trio = this.permissions.isBisualProductionTeamInCompanyProductAsTrio(String(this.company_product_id));
  readonly is_account_manager = this.permissions.isAccountManagerInCompanyProduct(String(this.company_product_id));
  readonly can_open_okr_management = this.is_product_trio || this.is_account_manager;

  private readonly current_month_key = formatMonthKeyFromDate(new Date());
  private readonly query_params = toSignal(this.activated_route.queryParamMap, {
    initialValue: this.activated_route.snapshot.queryParamMap,
  });

  readonly selected_month = computed((): string => {
    const query_month = this.query_params().get('month');
    return normalizeMonthKey(query_month) ?? this.current_month_key;
  });

  readonly selected_month_label = computed((): string => formatMonthLabelWithTodayHint(this.selected_month()));

  readonly can_go_to_next_month = computed((): boolean => this.selected_month() < this.current_month_key);
  readonly can_go_to_current_month = computed((): boolean => this.selected_month() !== this.current_month_key);

  /** Today for current month; last day of month for past months (day-based APIs). */
  readonly dashboard_date = computed((): Date => resolveDashboardDateForMonthKey(this.selected_month()));

  readonly active_company_objective = toSignal(
    toObservable(this.dashboard_date).pipe(
      switchMap(dashboard_date => this.api.getActiveCompanyObjective(this.company_product_id, dashboard_date))
    )
  );

  constructor() {
    this.activated_route.fragment.pipe(takeUntilDestroyed()).subscribe(fragment => {
      if (!fragment) {
        return;
      }

      requestAnimationFrame(() => {
        this.viewport_scroller.scrollToAnchor(fragment);
      });
    });
  }

  goToPreviousMonth(): void {
    this.updateMonthQueryParam(getPreviousMonthKey(this.selected_month()));
  }

  goToNextMonth(): void {
    this.updateMonthQueryParam(getNextMonthKey(this.selected_month()));
  }

  goToCurrentMonth(): void {
    this.updateMonthQueryParam(this.current_month_key);
  }

  goToCompanyObjectivesManagement(): void {
    this.router.navigate(['company-objectives'], { relativeTo: this.activated_route });
  }

  private updateMonthQueryParam(month: string): void {
    this.router.navigate([], {
      relativeTo: this.activated_route,
      queryParams: {
        month: month === this.current_month_key ? null : month,
      },
      queryParamsHandling: 'merge',
    });
  }
}
